import React, { useState, useEffect } from "react";
import Checkbox from "~/shared/ui/Fields/Checkbox";
import { isPermissionInCollection } from "~/shared/lib/permissionHelpers";
import s from "./AccordionPermissions.less";
import Back from "~/components/Icons/arrow-prev2";
import clsx from "clsx";

interface Permission {
  id?: string;
  accessDescriptionId?: string;
  description: string;
  moduleId: string;
}

interface Module {
  id: string;
  description: string;
  permissions: Permission[];
}

interface RoleActive {
  value: boolean;
  permissionDescriptionId: string;
}

interface Role {
  id: string;
  name: string;
  accesses?: RoleActive[];
  permissions?: RoleActive[];
}

interface UserModule {
  value: boolean;
  permissionDescriptionId: string;
}

interface Props {
  modules: Module[];
  roleActive: Role | null;
  title: string;
  userForm?: boolean;
  userModules?: UserModule[];
  savedPermissions?: Record<string, boolean>;
  checkedPermissions?: Record<string, boolean>;
  onPermissionsChange?: (checkedPermissions: Record<string, boolean>) => void;
  onSavedPermissions?: (checkedPermissions: Record<string, boolean>) => void;
}

const AccordionPermissionsUser: React.FC<Props> = ({
  modules,
  title,
  roleActive,
  userForm,
  userModules = [],
  savedPermissions = {},
  onPermissionsChange,
  onSavedPermissions,
}) => {
  const [openModule, setOpenModule] = useState<string | null>(null);
  const [checkedPermissions, setCheckedPermissions] = useState<
    Record<string, boolean>
  >({});

  const disabledPermissions = userForm
    ? new Set(
        roleActive?.permissions
          ?.filter((access) =>
            userModules.some(
              (userModule) =>
                userModule.permissionDescriptionId ===
                access.permissionDescriptionId
            )
          )
          .map((access) => access.permissionDescriptionId) || []
      )
    : new Set();

  useEffect(() => {
    const initialPermissions: Record<string, boolean> = {};
    modules.forEach((module) => {
      module.permissions.forEach((perm) => {
        initialPermissions[perm.id] = false;
      });
    });

    if (savedPermissions) {
      Object.keys(savedPermissions).forEach((permId) => {
        initialPermissions[permId] = savedPermissions[permId];
      });
    }

    if (roleActive) {
      if (roleActive?.permissions?.length > 0) {
        roleActive.permissions.forEach((access) => {
          initialPermissions[access.permissionDescriptionId] = access.value;
        });
      } else if (roleActive.accesses?.length > 0) {
        roleActive.accesses.forEach((access) => {
          initialPermissions[access.permissionDescriptionId] = access.value;
        });
      }
    }

    if (
      userModules &&
      (!roleActive ||
        (!roleActive.permissions?.length && !roleActive.accesses?.length))
    ) {
      userModules.forEach((userModule) => {
        initialPermissions[userModule.permissionDescriptionId] =
          userModule.value;
      });
    }

    setCheckedPermissions((prev) => {
      if (JSON.stringify(prev) === JSON.stringify(initialPermissions)) {
        return prev;
      }
      return initialPermissions;
    });
  }, [roleActive, modules, userModules, savedPermissions]);

  const toggleAccordion = (id: string) => {
    setOpenModule((prev) => (prev === id ? null : id));
  };

  const handleCheck = (permissionId: string, checked: boolean) => {
    setCheckedPermissions((prev) => {
      const newPermissions = { ...prev, [permissionId]: checked };
      onPermissionsChange?.(newPermissions);
      onSavedPermissions?.(newPermissions);
      return newPermissions;
    });
  };

  return (
    <div
      className={clsx(s.wrapper, {
        [s.wrapperUsers]: !!userForm,
      })}
    >
      {!userForm && <div className={s.title}>{title}</div>}
      {modules.map((module) => (
        <div key={module.id} className={s.accordion}>
          <div className={s.header} onClick={() => toggleAccordion(module.id)}>
            <span>{module.description}</span>
            <span className={s.arrow}>
              {openModule === module.id ? (
                <div className={s.top}>
                  <Back />
                </div>
              ) : (
                <div className={s.bottom}>
                  <Back />
                </div>
              )}
            </span>
          </div>

          {openModule === module.id && (
            <div className={s.body}>
              {module.permissions.map((perm) => {
                const isDisabledByRole = isPermissionInCollection(
                  roleActive?.permissions,
                  perm.id,
                );

                const isDisabled =
                  isDisabledByRole ||
                  (userForm && disabledPermissions.has(perm.id));

                return (
                  <div key={perm.id} className={s.permissionRow}>
                    <Checkbox
                      id={perm.id}
                      name={perm.id}
                      checked={checkedPermissions[perm.id] ?? false}
                      disabled={isDisabled}
                      onCheck={(val) => handleCheck(perm.id, val)}
                    />
                    <label
                      htmlFor={perm.id}
                      className={clsx(s.permissionLabel, {
                        [s.permissionLabelDisabled]: isDisabled,
                      })}
                    >
                      {perm.description}
                    </label>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default React.memo(AccordionPermissionsUser);
